import {Children, cloneElement, useRef, useState} from 'react';
import {SortableContainer, SortableElement, SortableHandle} from 'react-sortable-hoc';
import {MenuOutlined} from '@ant-design/icons';
import Table from './Table';
import {useMount} from '../../../hooks/useMount';
import styles from './SortableTable.module.scss';

const HandlePosition = {
  first: 'first',
  last: 'last',
};

const SortableTable = ({
  dataSource,
  columns,
  onOrderUpdated,
  showDragHandle,
  dragHandlePosition = HandlePosition.last,
  rowSelection,
  rowKey = 'key',
  loading,
  showHeader = true,
  ...props
}) => {
  const [visibleColumns, setVisibleColumns] = useState([]);
  const [columnWidths, setColumnWidths] = useState([]);
  const tableWrapper = useRef(null);

  const getVisibleColumns = () => {
    const cols = [];
    columns.forEach((col) => cols.push({...col, className: styles.dragVisible}));
    if (showDragHandle) {
      const sortColumn = {
        dataIndex: 'sort',
        className: styles.dragVisible,
        width: 50,
        align: dragHandlePosition === HandlePosition.first ? 'left' : 'center',
        render: () => <span className={styles.dragHandle}><DragHandle/></span>,
      };

      if (dragHandlePosition === HandlePosition.first) {
        cols.unshift(sortColumn);
      } else {
        cols.push(sortColumn);
      }
    }
    return cols;
  };

  const getCurrentColumnWidths = () => {
    const headerElement = tableWrapper.current.querySelector('thead tr');
    const colWidths = Array.from(headerElement.children).map((child) => child.clientWidth);
    const expectedCountOfColumns = columns.length + (rowSelection ? 1 : 0) + (showDragHandle ? 1 : 0);
    return colWidths.length === expectedCountOfColumns ? colWidths : [];
  };

  useMount(() => {
    setVisibleColumns(getVisibleColumns());
  });

  useMount(() => {
    if (showHeader) {
      setColumnWidths(getCurrentColumnWidths());
    }
  }, [visibleColumns, loading]);

  const DragHandle = SortableHandle(() => (
    <MenuOutlined style={{cursor: 'pointer', color: '#999'}}/>
  ));

  const TableRow = ({children, dragEnabled, ...restProps}) => (
    <tr {...restProps.props} className={dragEnabled ? null : styles.dragDisabled}>
      {Children.map(children,
        (c, index) => {
          const asDiv = cloneElement(c, {component: 'div'});
          const defaultWidth = dataSource.length === 0 ? '100%' : 'auto';
          return (<td style={{width: columnWidths[index] ? columnWidths[index] : defaultWidth}}>{asDiv}</td>);
        })}
    </tr>
  );

  const SortableTableRow = SortableElement((restProps) => (
    <TableRow dragEnabled {...restProps}/>
  ));

  const SortableWrapper = SortableContainer((restProps) => <tbody {...restProps}/>);

  const DraggableRow = ({className, style, ...restProps}) => {
    const index = dataSource.findIndex((x) => x[rowKey] === restProps['data-row-key']);
    const shouldShowHandle = typeof showDragHandle === 'function' ? showDragHandle(dataSource[index]) : showDragHandle;
    return shouldShowHandle
      ? <SortableTableRow index={index} {...restProps}/>
      : <TableRow {...restProps} dragEnabled={false}/>;
  };

  const DraggableWrapper = (restProps) => (
    <SortableWrapper
      helperClass={styles.rowDragging}
      helperContainer={() => tableWrapper.current.querySelector('tbody')}
      useDragHandle={!!showDragHandle}
      lockAxis={'y'}
      onSortEnd={onOrderUpdated || null}
      {...restProps}
    />
  );

  return (
    <div ref={tableWrapper} className={styles.sortableTable}>
      <Table
        showHeader={showHeader}
        pagination={false}
        dataSource={dataSource}
        columns={visibleColumns}
        rowSelection={rowSelection}
        rowKey={rowKey}
        components={{
          body: {
            wrapper: DraggableWrapper,
            row: DraggableRow,
          },
        }}
        loading={loading}
        {...props}
      />
    </div>
  );
};

export default SortableTable;
